import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostBinding,
} from '@angular/core';

import {
  NbCalendarModule,
  NbCardModule,
  NbButtonModule,
  NbIconModule,
  NbDateService,
  NbRadioModule,
  NbCalendarRange,
  NbCalendarRangeModule,
  NbSelectModule,
  NbInputModule,
  NbDatepickerModule
} from '@nebular/theme';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { AttendanceDayCellComponent } from './attendance-day-cell.component';
import { Attendance, AttendanceService } from './attendance.service';
import { DropDownData } from '../ai-chatwoot-buddy.component';
import { LeaveDayCellComponent } from './leave-day-cell.component';
import { NbEvaIconsModule } from '@nebular/eva-icons';

@Component({
  selector: 'ltts-attendance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbCalendarModule,
    NbButtonModule,
    NbIconModule,
    NbRadioModule,
    NbCalendarRangeModule,
    NbSelectModule,
    NbInputModule,
    NbDatepickerModule,
    NbEvaIconsModule,
    NbButtonModule
  ],
  templateUrl: './attendance.component.html',
  styleUrls: ['./attendance.component.scss'],
  providers: [AttendanceService],
})
export class AttendanceComponent implements OnInit, OnDestroy {
  @Input() attendanceData: Attendance[] = [];
  @Input() disabled = false;
  @Input() type: 'regularization' | 'leave'='regularization';
  @Input() dropdown!:DropDownData[];

  @Output() selectedDatesChange = new EventEmitter<string[]>();
  @Output() optionChange = new EventEmitter<string>();
  @Output() promptChange = new EventEmitter<string>();
  @Output() fileChange = new EventEmitter<{file: File, binary: string}>();
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() componentDataChange = new EventEmitter<any>();

  @HostBinding('class.disabled')
  get isDisabled() {
    return this.disabled;
  }

  dayCellComponent = AttendanceDayCellComponent;
  leaveCellComponent=LeaveDayCellComponent;

  minDate!: Date;
  maxDate!: Date;
  viewDate: Date;

  selectedLeaveType!:any;

  option: string | null = null;

  range: NbCalendarRange<Date>;
  regularizationFromDate: string = '';
  regularizationToDate: string = '';
  regularizationSelectedDates: string = '';

  leaveReason: string = '';

  selectedFile: File | null = null;

  fileBinary: string | null = null;

  modelResponse: string = '';
  isLoadingModel: boolean = false;

  private subscription = new Subscription();

  options = [
    { value: 'leave', label: 'Apply Leave' },
    { value: 'attendance', label: 'Regularize Attendance' },
  ];

  constructor(
    private attendanceService: AttendanceService,
    protected dateService: NbDateService<Date>
  ) {
    this.viewDate = this.dateService.today();
    this.range = { start: undefined as any, end: undefined };
  }

  ngOnInit(): void {
    this.attendanceService.setType(this.type);
    if (this.attendanceData?.length) {
      this.attendanceService.setAttendanceData(this.attendanceData);
      const dates = this.attendanceData.map(d => {
        const [y, m, d2] = d.date.split('-').map(Number);
        return new Date(y, m - 1, d2);
      });

      this.minDate = new Date(Math.min(...dates.map(d => d.getTime())));
      this.maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
      this.viewDate = this.minDate;
    }

    this.subscription.add(
      this.attendanceService.selectedDates$.subscribe(dates => {
        const sortedDates = this.sortDisplayDates(dates);
        this.syncRegularizationInputs(sortedDates);
        this.syncRangeFromRegularizationSelection(sortedDates);
        this.selectedDatesChange.emit(sortedDates);
      })
    );

    this.attendanceService.setLeaveRange(this.range);

    // Initialize validation state
    setTimeout(() => {
      this.emitPrompt();
      this.emitComponentData();
    }, 100);
  }
    onValChange(){      
      this.emitPrompt();
    }

    dayAndTime(){
      const date=new Date();
      const daysOfWeek = [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ];
      const dayOfWeek = daysOfWeek[date. getDay()];
      const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'long' });
      return `${dayOfWeek}, ${day} ${month}`
    }

    onFileChange(event: any): void {
  const file = event.target.files[0];
  if (file) {
    this.selectedFile = file;
    this.convertFileToBinary(file);
  } else {
    this.selectedFile = null;
    this.fileBinary = null;
    this.emitPrompt(); // re-evaluate prompt
  }
}


    convertFileToBinary(file: File): void {
  const reader = new FileReader();
  reader.onload = (e) => {
    const result = e.target?.result as string;
    this.fileBinary = result.split(',')[1];

    this.fileChange.emit({ file, binary: this.fileBinary });

    // 🔥 Important: Generate prompt after file is ready
    this.emitPrompt();
  };
  reader.readAsDataURL(file);
}


    generateLeavePrompt(selectedDates?: string[]): string {
      
      // Check if file is required but not uploaded - in that case, don't generate prompt
      if (this.isFileUploadRequired() && !this.selectedFile) {
        return '';
      }
      
      const hasLeaveType = !!this.selectedLeaveType;
      if (!hasLeaveType) {
        return '';
      }
      
      const leaveType = this.selectedLeaveType?.name || '';
      const reason = this.leaveReason || '';
      let prompt = `Apply leave`;
      let datesPrompt = '';

      if (selectedDates && selectedDates.length > 0) {
          if (selectedDates.length === 1) {
              datesPrompt = ` on ${selectedDates[0]}`;
          } else {
              datesPrompt = ` for ${selectedDates.join(', ')}`;
          }
      } else if (this.range?.start) {
          const startDate = this.formatDisplay(this.range.start);
          if (this.range.end) {
              const endDate = this.formatDisplay(this.range.end);
              if (startDate === endDate) {
                  datesPrompt = ` on ${startDate}`;
              } else {
                  datesPrompt = ` from ${startDate} to ${endDate}`;
              }
          } else {
              datesPrompt = ` on ${startDate}`;
          }
      }
      prompt += datesPrompt;
      if(leaveType){
        prompt += `; Type = ${leaveType}`
      }

      if (!datesPrompt) {
          return '';
      }

      
      
      if (reason) {
        prompt += `; Reason = ${reason}`;
      }

      return prompt;
    }

    isFileUploadRequired(): boolean {
      const isRequired = this.selectedLeaveType?.attachment_required === true;
      return isRequired;
     }

    canSubmitPrompt(): boolean {
      // For leave type: require date range + leave type + file if required
      if (this.type === 'leave') {
        const hasValidRange = !!(this.range?.start && this.range?.end);
        const hasLeaveType = !!this.selectedLeaveType;
        const hasFileIfRequired = !this.isFileUploadRequired() || !!this.selectedFile;
        
        return hasValidRange && hasLeaveType && hasFileIfRequired;
      }
      
      // For regularization type: require dates and option
      if (this.type === 'regularization') {
        const selectedDates = this.attendanceService.getSelectedDates();
        return selectedDates.length > 0 && !!this.option;
      }
      
      return false;
    }

    canSubmitLeave(): boolean {
      // This is stricter - requires file when needed for actual submission
      if (!this.range.start || !this.selectedLeaveType) {
        return false;
      }
      
      if (this.isFileUploadRequired() && !this.selectedFile) {
        return false;
      }
      
      return true;
    }


    emitPrompt(): void {
      let prompt = '';
      const isValid = this.canSubmitPrompt();
      
      // Debug logging

      
      // Emit validation state and component data for UI feedback
      this.validationChange.emit(isValid);
      this.emitComponentData();
      
      // For leave type: always generate prompt if basic conditions met
      if (this.type === 'leave') {
        prompt = this.generateLeavePrompt();
      }

      // For regularization type: require dates and option
      else if (this.type === 'regularization' && this.option) {
        const selectedDates = this.getSortedSelectedDates();
        if (selectedDates.length > 0) {
          if (this.option === 'attendance') {
            prompt = `Regularize Attendance for ${selectedDates.join(', ')}`;
          } else if (this.option === 'leave') {
            prompt=this.generateLeavePrompt(selectedDates)
          }
        }
      }
      
      this.promptChange.emit(prompt);
    }

    emitComponentData(): void {
      let prompt = '';
      if (this.type === 'leave') {
        prompt = this.generateLeavePrompt();
      } else if (this.type === 'regularization' && this.option) {
        const selectedDates = this.getSortedSelectedDates();
        if (selectedDates.length > 0) {
          if (this.option === 'attendance') {
            prompt = `Regularize Attendance for ${selectedDates.join(', ')}`;
          } else if (this.option === 'leave') {
            prompt=this.generateLeavePrompt(selectedDates)
          }
        }
      }

      // Emit component data that parent needs for validation
      const componentData = {
        type: this.type,
        prompt: prompt,
        range: this.range,
        selectedLeaveType: this.selectedLeaveType,
        selectedOption: this.option,
        selectedDates: this.type === 'regularization' ? this.getSortedSelectedDates() : [],
        isFileUploadRequired: this.isFileUploadRequired(),
        selectedFile: this.selectedFile,
        isValidated: this.canSubmitPrompt()
      };
      
      this.componentDataChange.emit(componentData);
    }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

   onOptionChange(value: string): void {
    this.option = value;

    if (this.type === 'regularization' && value === 'leave') {
      const selectedDates = this.getSortedSelectedDates();
      if (!this.isContinuousWeekdaySelection(selectedDates)) {
        this.attendanceService.setSelectedDates([]);
      } else {
        this.syncRangeFromRegularizationSelection(selectedDates);
      }
    }

    this.optionChange.emit(value);
    this.emitPrompt();
  }

  handleRangeChange(range: NbCalendarRange<Date>): void {
    const normalizedRange = this.normalizeRange(range);
    this.range = normalizedRange;

    if (normalizedRange?.start && normalizedRange?.end) {
      this.attendanceService.setHoverDate(null);
    }

    this.attendanceService.setLeaveRange(normalizedRange);
    this.emitPrompt();
  }

  onLeaveStartDateChange(date: Date | null): void {
    const start = this.normalizeInputDate(date) ?? undefined;
    const nextRange: NbCalendarRange<Date> = {
      start: start as any,
      end: this.range?.end,
    };

    this.handleRangeChange(nextRange);
  }

  onLeaveEndDateChange(date: Date | null): void {
    const normalizedEndDate = this.normalizeInputDate(date);
    const end = normalizedEndDate ?? undefined;
    const start = this.range?.start ?? normalizedEndDate ?? undefined;

    const nextRange: NbCalendarRange<Date> = {
      start: start as any,
      end,
    };

    this.handleRangeChange(nextRange);
  }

  get leaveStartInputValue(): string {
    return this.range?.start ? this.formatDisplay(this.range.start) : '';
  }

  get leaveEndInputValue(): string {
    return this.range?.end ? this.formatDisplay(this.range.end) : '';
  }

  get isRegularizationLeaveMode(): boolean {
    return this.type === 'regularization' && this.option === 'leave';
  }

  get leaveTotalDays(): number {
    if (!this.range?.start) {
      return 0;
    }

    const startDate = this.stripTime(this.range.start);
    const endDate = this.stripTime(this.range.end ?? this.range.start);
    const [fromDate, toDate] =
      startDate <= endDate ? [startDate, endDate] : [endDate, startDate];

    let totalDays = 0;
    const currentDate = new Date(fromDate);

    while (currentDate <= toDate) {
      if (!this.isWeekend(currentDate)) {
        totalDays += 1;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return totalDays;
  }

  onDateChange(date: Date): void {
    if (this.disabled || !this.isSelectableRegularizationDate(date)) {
      return;
    }
    console.log("Date",date)

    const displayDate = this.formatDisplay(date);
    const selectedDates = this.getSortedSelectedDates();
    const isAlreadySelected = selectedDates.includes(displayDate);

    const nextSelection = isAlreadySelected
      ? selectedDates.filter((d) => d !== displayDate)
      : this.sortDisplayDates([...selectedDates, displayDate]);

    if (this.option === 'leave' && !this.isContinuousWeekdaySelection(nextSelection)) {
      if (!isAlreadySelected) {
        // For leave mode, non-contiguous click starts a new valid sequence.
        this.attendanceService.setSelectedDates([displayDate]);
        this.emitPrompt();
      }
      return;
    }

    this.attendanceService.setSelectedDates(nextSelection);
    this.emitPrompt();
  }

  private syncRegularizationInputs(dates: string[]): void {
    this.regularizationSelectedDates = dates.join(', ');
    this.regularizationFromDate = dates[0] ?? '';
    this.regularizationToDate = dates[dates.length - 1] ?? '';
  }

  private syncRangeFromRegularizationSelection(dates: string[]): void {
    if (!(this.type === 'regularization' && this.option === 'leave')) {
      return;
    }

    let nextRange: NbCalendarRange<Date>;

    if (!dates.length) {
      nextRange = { start: undefined as any, end: undefined };
    } else if (dates.length === 1) {
      const onlyDate = this.parseDisplayDate(dates[0]);
      nextRange = { start: onlyDate as any, end: onlyDate };
    } else {
      const startDate = this.parseDisplayDate(dates[0]);
      const endDate = this.parseDisplayDate(dates[dates.length - 1]);
      nextRange = { start: startDate as any, end: endDate };
    }

    this.range = nextRange;
    this.attendanceService.setLeaveRange(nextRange);
  }

  private normalizeInputDate(value: Date | null): Date | null {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return null;
    }

    return value;
  }

  private stripTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private normalizeRange(range: NbCalendarRange<Date>): NbCalendarRange<Date> {
    const start = range?.start;
    const end = range?.end;

    if (!start || !end || start <= end) {
      return range;
    }

    return { start: end as any, end: start };
  }

  private isSelectableRegularizationDate(date: Date): boolean {
    if (this.isWeekend(date)) {
      return false;
    }
    console.log("Date",date)
    console.log(this.formatISO(date))
    console.log("Att data",this.attendanceData)

    const record = this.attendanceData?.find(
      (attendance) => attendance.date === this.formatISO(date)
    );
    console.log("Record",record)

    return this.isMissPunchStatus(record?.workingStatus);
  }

  private isMissPunchStatus(status?: string): boolean {
    if (!status) {
      return false;
    }

    const normalizedStatus = status.toLowerCase().replace(/[^a-z]/g, '');
    return normalizedStatus.includes('misspunch');
  }

  private isContinuousWeekdaySelection(dates: string[]): boolean {
    if (dates.length <= 1) {
      return true;
    }

    const sortedDates = this.sortDisplayDates(dates);

    for (let i = 1; i < sortedDates.length; i++) {
      const previousDate = this.parseDisplayDate(sortedDates[i - 1]);
      const currentDate = this.parseDisplayDate(sortedDates[i]);

      if (!this.isNextWeekday(previousDate, currentDate)) {
        return false;
      }
    }

    return true;
  }

  private isNextWeekday(previousDate: Date, currentDate: Date): boolean {
    const expectedDate = new Date(previousDate);

    do {
      expectedDate.setDate(expectedDate.getDate() + 1);
    } while (this.isWeekend(expectedDate));

    return this.isSameDay(expectedDate, currentDate);
  }

  private isSameDay(leftDate: Date, rightDate: Date): boolean {
    return (
      leftDate.getFullYear() === rightDate.getFullYear() &&
      leftDate.getMonth() === rightDate.getMonth() &&
      leftDate.getDate() === rightDate.getDate()
    );
  }

  private isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  private getSortedSelectedDates(): string[] {
    return this.sortDisplayDates(this.attendanceService.getSelectedDates());
  }

  private sortDisplayDates(dates: string[]): string[] {
    return [...dates].sort(
      (leftDate, rightDate) =>
        this.parseDisplayDate(leftDate).getTime() -
        this.parseDisplayDate(rightDate).getTime()
    );
  }

  private parseDisplayDate(displayDate: string): Date {
    const [dayText, monthText, yearText] = displayDate.split('-');
    const monthIndex = new Date(`${monthText} 1, 2000`).getMonth();
    const day = Number(dayText);
    const year = 2000 + Number(yearText);

    return new Date(year, monthIndex, day);
  }



  private formatISO(date: Date): string {
    const y = this.dateService.getYear(date);
    const m = String(this.dateService.getMonth(date) + 1).padStart(2, '0');
    const d = String(this.dateService.getDate(date)).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatDisplay(date: Date): string {
    const d = String(this.dateService.getDate(date)).padStart(2, '0');
    const m = date.toLocaleString('en-US', { month: 'short' });
    const y = this.dateService.getYear(date).toString().slice(-2);
    return `${d}-${m}-${y}`;
  }
}
